import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Stack, Paper, Button,
    TextField, MenuItem, Alert, IconButton, CircularProgress
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const PARCHMENT  = '#F7F3EE';

const BIKE_TYPES = ['ELECTRIC', 'ROAD', 'MOUNTAIN', 'NORMAL'];
const CONDITIONS = ['NEW', 'GOOD', 'NEEDS_REPAIR', 'BROKEN'];

export default function CreateBicycle() {
    const { user }   = useAuth();
    const navigate   = useNavigate();
    const location   = useLocation();
    const { t }      = useTranslation();

    const isAdmin   = user?.role === 'ADMIN';
    const isManager = user?.role === 'MANAGER';

    const stateDistrictId   = location.state?.districtId;
    const stateDistrictName = location.state?.districtName;

    const [form, setForm] = useState({
        type:       '',
        condition:  '',
        districtId: stateDistrictId || '',
        quantity:   '1',
    });
    const [districts,    setDistricts]    = useState([]);
    const [loadingDist,  setLoadingDist]  = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [error,        setError]        = useState('');

    useEffect(() => {
        if (user && !isAdmin && !isManager) navigate('/', { replace: true });
    }, [user, isAdmin, isManager, navigate]);

    useEffect(() => {
        if (isAdmin) {
            setLoadingDist(true);
            axios.get('/districts/view?size=1000')
                .then(res => setDistricts(Array.isArray(res.data) ? res.data : (res.data?.content || [])))
                .catch(() => {})
                .finally(() => setLoadingDist(false));
        }
    }, [isAdmin]);

    const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.type || !form.condition) {
            setError(t('bicycle.fill_required', 'Please select a type and condition.'));
            return;
        }
        if (isAdmin && !form.districtId) {
            setError(t('bicycle.select_district', 'Please select a district.'));
            return;
        }
        const qty = Number(form.quantity);
        if (!form.quantity || isNaN(qty) || qty < 1 || qty > 50) {
            setError(t('bicycle.quantity_range_error', 'Quantity must be between 1 and 50.'));
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = { type: form.type, condition: form.condition, quantity: qty };
            if (isAdmin) payload.districtId = form.districtId;
            await axios.post('/bicycles/bicycle', payload);
            const targetDistrict = isAdmin ? form.districtId : stateDistrictId;
            if (targetDistrict) {
                navigate(`/districts/${targetDistrict}`);
            } else {
                navigate(-1);
            }
        } catch {
            setError(t('bicycle.create_failed', 'Failed to create bicycle. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    if (!user || (!isAdmin && !isManager)) return null;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT }}>
            {/* Banner */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${TERRACOTTA} 100%)`,
                height: 130, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '40px', background: PARCHMENT, borderRadius: '40px 40px 0 0' },
            }} />

            <Container maxWidth="sm" sx={{ mt: -4, pb: 8, position: 'relative', zIndex: 1 }}>

                {/* Back row */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <IconButton onClick={() => navigate(-1)} size="small"
                        sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.2)', borderRadius: 2 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="overline" sx={{ color: FOREST, letterSpacing: '0.2em', fontWeight: 700 }}>
                        {t('bicycle.overline', 'Bicycle Management')}
                    </Typography>
                </Stack>

                <Paper elevation={0} sx={{
                    p: 3, borderRadius: 3, border: '1px solid rgba(196,98,45,0.1)',
                    bgcolor: 'white', boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                        <DirectionsBikeIcon sx={{ color: TERRACOTTA }} />
                        <Typography variant="h6" fontWeight={800}
                            sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {t('bicycle.create_title', 'Add New Bicycle')}
                        </Typography>
                    </Stack>

                    {isManager && stateDistrictName && (
                        <Typography variant="body2" sx={{ mb: 2.5, color: 'text.secondary' }}>
                            {t('bicycle.district_label', 'District')}:{' '}
                            <Box component="span" fontWeight={700} sx={{ color: WALNUT }}>{stateDistrictName}</Box>
                        </Typography>
                    )}

                    <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>

                        {isAdmin && (
                            <TextField
                                label={t('bicycle.district', 'District')}
                                value={form.districtId}
                                onChange={e => set('districtId', e.target.value)}
                                select fullWidth required
                                disabled={loadingDist}
                                InputProps={loadingDist ? { endAdornment: <CircularProgress size={18} /> } : {}}>
                                <MenuItem value=""><em>{t('bicycle.select_district_placeholder', '— Select district —')}</em></MenuItem>
                                {districts.map(d => (
                                    <MenuItem key={d.id} value={d.id}>
                                        {d.name || d.region || d.id}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        <TextField
                            label={t('bicycle.type', 'Bike Type')}
                            value={form.type}
                            onChange={e => set('type', e.target.value)}
                            select fullWidth required>
                            <MenuItem value=""><em>{t('bicycle.select_type', '— Select type —')}</em></MenuItem>
                            {BIKE_TYPES.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label={t('bicycle.condition', 'Condition')}
                            value={form.condition}
                            onChange={e => set('condition', e.target.value)}
                            select fullWidth required>
                            <MenuItem value=""><em>{t('bicycle.select_condition', '— Select condition —')}</em></MenuItem>
                            {CONDITIONS.map(c => (
                                <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label={t('bicycle.quantity', 'Quantity')}
                            type="number"
                            value={form.quantity}
                            onChange={e => set('quantity', e.target.value)}
                            fullWidth
                            inputProps={{ min: 1, max: 50 }}
                            error={form.quantity !== '' && (Number(form.quantity) < 1 || Number(form.quantity) > 50 || isNaN(Number(form.quantity)))}
                            helperText={
                                form.quantity !== '' && (Number(form.quantity) < 1 || Number(form.quantity) > 50 || isNaN(Number(form.quantity)))
                                    ? t('bicycle.quantity_range_error', 'Quantity must be between 1 and 50.')
                                    : t('bicycle.quantity_hint', 'How many identical bicycles to add (1–50). Codes are assigned later.')
                            }
                        />

                        {error && <Alert severity="error">{error}</Alert>}

                        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                            <Button onClick={() => navigate(-1)} disabled={saving}>
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" variant="contained" disabled={saving}
                                sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                                {saving ? t('common.saving') : t('bicycle.create_btn', 'Add Bicycle')}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}
